/**
 * Mixpanel event constants and callback contract for AskAsperAgentic.
 *
 * Consumers pass a single `onTrackEvent(eventName, eventProperty?)` callback
 * (matches the host app's `trackEvent` helper). Use `createMixpanelEventFirer`
 * inside components to obtain a safe `(eventName, eventProperty?) => void`
 * function that no-ops when the callback is not supplied.
 */

// ===== Callback contract =====
export type MixpanelEventProperty = Record<string, unknown>;

export type MixpanelTrackEventCallback = (eventName: string, eventProperty?: MixpanelEventProperty) => void;

/**
 * Returns a single `fire(eventName, eventProperty?)` function that:
 *  - calls `onTrackEvent` when provided
 *  - else no-ops
 */
export function createMixpanelEventFirer(
    onTrackEvent: MixpanelTrackEventCallback | undefined,
): MixpanelTrackEventCallback {
    return (eventName, eventProperty) => {
        if (!eventName || !onTrackEvent) return;
        onTrackEvent(eventName, eventProperty);
    };
}

/**
 * Privacy-first text length helper. Use when an event spec says
 * "search text if approved to pass; otherwise just text length".
 */
export function getTextLength(text: string | undefined | null): number {
    if (!text) return 0;
    return text.length;
}

// ===== Event name constants =====

// ----- Global -----
export const ASK_ASPER_ICON_CLICK = 'ask_asper_global_icon_click';

// ----- Landing Page -----
export const LANDING_PAGE_VIEW = 'ask_asper_landing_page_load';
export const LANDING_PAGE_QUESTION_BANK_CLICK = 'ask_asper_landing_question_bank_click';
export const LANDING_PAGE_SAVED_QUESTIONS_CLICK = 'ask_asper_landing_saved_questions_click';
export const LANDING_PAGE_PROMPT_PILL_CLICK = 'ask_asper_landing_prompt_pill_click';
export const LANDING_PAGE_SEND_CLICK = 'ask_asper_landing_send_click';
export const LANDING_PAGE_THREAD_PANEL_CLICK = 'ask_asper_landing_thread_panel_click';
export const LANDING_PAGE_INPUT_FOCUS = 'ask_asper_landing_input_focus';

// ----- Thread Panel -----
export const THREAD_PANEL_VIEW = 'ask_asper_thread_panel_load';
export const THREAD_PANEL_THREAD_CLICK = 'ask_asper_thread_open';
export const THREAD_PANEL_THREAD_EDIT = 'ask_asper_thread_rename_save';
export const THREAD_PANEL_THREAD_DELETE = 'ask_asper_thread_delete_confirm';
export const THREAD_PANEL_SEARCH = 'ask_asper_thread_panel_search_run';
export const THREAD_PANEL_SEARCH_CLOSE = 'ask_asper_thread_panel_search_clear';
export const THREAD_PANEL_NEW_CHAT_CLICK = 'ask_asper_new_chat_click';

// ----- Question Bank Modal -----
export const QUESTION_BANK_VIEW = 'ask_asper_question_bank_modal_load';
export const QUESTION_BANK_SECTION_CLICK = 'ask_asper_question_bank_section_click';
export const QUESTION_BANK_SEARCH = 'ask_asper_question_bank_search_run';
export const QUESTION_BANK_SEARCH_CLOSE = 'ask_asper_question_bank_search_clear';
export const QUESTION_BANK_QUESTION_CLICK = 'ask_asper_question_bank_question_click';
export const QUESTION_BANK_SAVED_QUESTION_EDIT = 'ask_asper_saved_question_edit_save';
export const QUESTION_BANK_SAVED_QUESTION_DELETE = 'ask_asper_saved_question_delete_confirm';

// ----- Left-Right Conversation Panel -----
export const CONVERSATION_PANEL_VIEW = 'ask_asper_conversation_panel_load';

// ----- Left Panel -----
export const LEFT_PANEL_HIDE_CLICK = 'ask_asper_left_panel_hide_click';
export const LEFT_PANEL_RESIZE = 'ask_asper_left_panel_resize';
export const LEFT_PANEL_SHOW_TASK_CLICK = 'ask_asper_show_task_click';
export const LEFT_PANEL_VIEW_ARTEFACT_CLICK = 'ask_asper_view_artefact_click';
export const LEFT_PANEL_FOLLOW_UP_PILL_CLICK = 'ask_asper_follow_up_pill_click';

// ----- Right Panel: View Modes -----
export const RIGHT_PANEL_PRESENTATION_MODE_CLICK = 'ask_asper_presentation_mode_click';
export const RIGHT_PANEL_LIST_MODE_CLICK = 'ask_asper_list_mode_click';
export const RIGHT_PANEL_PRESENTATION_ARROW_CLICK = 'ask_asper_artefact_navigation_arrow_click';

// ----- Right Panel: Multi-Select -----
export const RIGHT_PANEL_MULTI_SELECT_CLICK = 'ask_asper_multi_select_click';
export const RIGHT_PANEL_MULTI_SELECT_SELECT_ALL_CLICK = 'ask_asper_multi_select_select_all_click';
export const RIGHT_PANEL_MULTI_SELECT_DESELECT_ALL_CLICK = 'ask_asper_multi_select_deselect_all_click';
export const RIGHT_PANEL_MULTI_SELECT_EXIT_CLICK = 'ask_asper_multi_select_exit_click';
export const RIGHT_PANEL_MULTI_SELECT_ARTEFACT_TOGGLE = 'ask_asper_multi_select_artefact_selection_click';
export const RIGHT_PANEL_MULTI_SELECT_EXPORT_CLICK = 'ask_asper_multi_select_export_click';
export const RIGHT_PANEL_MULTI_SELECT_EXPORT_OPTION_CLICK = 'ask_asper_multi_select_export_option_click';

// ----- Right Panel: Individual Artefact Actions -----
export const RIGHT_PANEL_METHODOLOGY_EXPAND_CLICK = 'ask_asper_artefact_methodology_expand';
export const RIGHT_PANEL_METHODOLOGY_COLLAPSE_CLICK = 'ask_asper_artefact_methodology_collapse';
export const RIGHT_PANEL_ARTEFACT_EXPORT_CLICK = 'ask_asper_artefact_export_click';
export const RIGHT_PANEL_ARTEFACT_EXPORT_OPTION_CLICK = 'ask_asper_artefact_export_option_click';
export const RIGHT_PANEL_ARTEFACT_THUMBS_UP_CLICK = 'ask_asper_artefact_feedback_thumbs_up';
export const RIGHT_PANEL_ARTEFACT_THUMBS_DOWN_CLICK = 'ask_asper_artefact_feedback_thumbs_down';
export const RIGHT_PANEL_ARTEFACT_FEEDBACK_TEXT_SUBMIT = 'ask_asper_artefact_feedback_text_submit';

// ----- Right Panel: Charts -----
export const RIGHT_PANEL_CHART_SCROLL_CLICK = 'ask_asper_chart_scroll';
export const RIGHT_PANEL_CHART_SLIDER_CLICK = 'ask_asper_chart_slider_use';
export const RIGHT_PANEL_CHART_FILTER_CLICK = 'ask_asper_chart_filter_click';
export const RIGHT_PANEL_CHART_FILTER_DROPDOWN_OPEN = 'ask_asper_chart_filter_dropdown_open';
export const RIGHT_PANEL_CHART_FILTER_DROPDOWN_SELECT = 'ask_asper_chart_filter_selection';
export const RIGHT_PANEL_CHART_FILTER_DROPDOWN_DESELECT = 'ask_asper_chart_filter_deselection';

// ===== Chart interaction event contract =====
/**
 * Generic chart interaction events emitted by chart components (EnhancedBarChart,
 * EnhancedLineChart, EnhancedStackedBarChart, etc.) so that hosts can map them to
 * their own analytics. Inside AskAsperAgentic these are routed to the right
 * `RIGHT_PANEL_CHART_*` mixpanel events.
 */
export type ChartTrackEvent =
    | { type: 'scroll'; chartTitle?: string; chartType?: string; direction?: 'forward' | 'backward' }
    | { type: 'slider_use'; chartTitle?: string; chartType?: string; range: [number, number] }
    | { type: 'filter_click'; chartTitle?: string; chartType?: string; filter_name: string }
    | { type: 'filter_dropdown_open'; chartTitle?: string; chartType?: string; filter_name: string }
    | { type: 'filter_select'; chartTitle?: string; chartType?: string; filter_name: string; values: unknown[] }
    | { type: 'filter_deselect'; chartTitle?: string; chartType?: string; filter_name: string; values: unknown[] };

export type ChartTrackEventCallback = (event: ChartTrackEvent) => void;

/**
 * Maps a `ChartTrackEvent` (emitted from a generic chart component) into a fired
 * AskAsper `RIGHT_PANEL_CHART_*` mixpanel event using the supplied `fire`
 * helper. Returns a function suitable for passing as `onTrackChartEvent`.
 */
export function createChartEventForwarder(fire: MixpanelTrackEventCallback): ChartTrackEventCallback {
    return (event) => {
        const baseProps: MixpanelEventProperty = {};
        if (event.chartTitle) baseProps.chart_title = event.chartTitle;
        if (event.chartType) baseProps.chart_type = event.chartType;

        switch (event.type) {
            case 'scroll':
                fire(RIGHT_PANEL_CHART_SCROLL_CLICK, {
                    ...baseProps,
                    ...(event.direction ? { direction: event.direction } : {}),
                });
                return;
            case 'slider_use':
                fire(RIGHT_PANEL_CHART_SLIDER_CLICK, {
                    ...baseProps,
                    range: event.range,
                });
                return;
            case 'filter_click':
                fire(RIGHT_PANEL_CHART_FILTER_CLICK, {
                    ...baseProps,
                    filter_name: event.filter_name,
                });
                return;
            case 'filter_dropdown_open':
                fire(RIGHT_PANEL_CHART_FILTER_DROPDOWN_OPEN, {
                    ...baseProps,
                    filter_name: event.filter_name,
                });
                return;
            case 'filter_select':
                fire(RIGHT_PANEL_CHART_FILTER_DROPDOWN_SELECT, {
                    ...baseProps,
                    filter_name: event.filter_name,
                    values: event.values,
                });
                return;
            case 'filter_deselect':
                fire(RIGHT_PANEL_CHART_FILTER_DROPDOWN_DESELECT, {
                    ...baseProps,
                    filter_name: event.filter_name,
                    values: event.values,
                });
                return;
        }
    };
}
